import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon, PlayIcon } from '@heroicons/react/24/outline';
import DemoVideoModal from './DemoVideoModal';
import { track } from '../../utils/analytics';
import { AnalyticsEvents } from '../../utils/analyticsEvents';
import './LandingCtaSection.css';

interface LandingCtaSectionProps {
  user: { email?: string } | null;
  onStartTrial: () => void;
}

const LandingCtaSection: React.FC<LandingCtaSectionProps> = ({
  user,
  onStartTrial,
}) => {
  const [showDemoVideo, setShowDemoVideo] = useState(false);

  const handleWatchDemo = () => {
    track(AnalyticsEvents.LANDING_DEMO_CLICK);
    setShowDemoVideo(true);
  };

  return (
    <section className="landing-cta relative overflow-hidden text-white py-8 sm:py-10 lg:py-14">
      <div className="absolute inset-0 overflow-hidden">
        <motion.div
          className="absolute -right-16 -top-16 w-48 h-48 sm:-right-24 sm:-top-24 sm:w-72 sm:h-72 lg:-right-32 lg:-top-32 lg:w-96 lg:h-96 bg-blue-500/4 rounded-full mix-blend-soft-light"
          animate={{
            scale: [1, 1.1, 1],
            opacity: [0.3, 0.6, 0.3],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
        <motion.div
          className="absolute -left-16 -bottom-16 w-48 h-48 sm:-left-24 sm:-bottom-24 sm:w-72 sm:h-72 lg:-left-32 lg:-bottom-32 lg:w-96 lg:h-96 bg-purple-500/4 rounded-full mix-blend-soft-light"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.2, 0.5, 0.2],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 2,
          }}
        />
        <div className="absolute inset-0">
          {[...Array(15)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-1 h-1 bg-sky-400/18 rounded-full"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                y: [0, -30, 0],
                opacity: [0.2, 0.8, 0.2],
                scale: [0.5, 1, 0.5],
              }}
              transition={{
                duration: 4 + Math.random() * 3,
                repeat: Infinity,
                delay: Math.random() * 3,
              }}
            />
          ))}
        </div>
        <div className="absolute inset-0 bg-grid-white/[0.015] bg-[length:20px_20px]"></div>
        <div className="absolute inset-0 bg-slate-950/40"></div>
      </div>
      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <span className="inline-flex items-center px-3 sm:px-4 py-1.5 sm:py-2 rounded-md text-xs sm:text-sm font-medium bg-white/5 border border-white/10 text-slate-300 mb-4 sm:mb-6">
            <span className="h-1.5 w-1.5 sm:h-2 sm:w-2 rounded-full bg-emerald-400 shadow-[0_0_6px_rgba(52,211,153,0.5)] mr-1.5 sm:mr-2"></span>
            Ready to get started?
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6">
            <span className="block text-white">Transform your sales process</span>
            <span className="bg-gradient-to-r from-cyan-300 via-sky-400 to-cyan-400 bg-clip-text text-transparent">
              with AI-powered calling
            </span>
          </h2>
          <p className="text-sm sm:text-base lg:text-xl text-slate-400 max-w-2xl mx-auto mb-6 sm:mb-8">
            Start free today, test the workflow with real leads, and scale when your team is ready.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            <button
              onClick={onStartTrial}
              className="px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-cyan-400 to-sky-500 hover:from-cyan-300 hover:to-sky-400 text-white font-medium rounded-lg transition-all duration-300 transform hover:-translate-y-0.5 hover:shadow-xl hover:shadow-cyan-500/25 flex items-center justify-center text-sm sm:text-base shadow-lg shadow-cyan-500/20"
            >
              {user ? 'Go to Dashboard' : 'Start with 20 Free Credits'}
              <ArrowRightIcon className="ml-2 h-4 w-4 sm:h-5 sm:w-5" />
            </button>
            <button
              onClick={handleWatchDemo}
              className="px-6 sm:px-8 py-3 sm:py-4 bg-white/5 hover:bg-white/15 backdrop-blur-sm text-white font-medium rounded-lg transition-all duration-300 border border-white/20 hover:border-white/30 flex items-center justify-center text-sm sm:text-base"
            >
              <PlayIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
              Watch Video
            </button>
          </div>
          <p className="mt-4 sm:mt-6 text-xs sm:text-sm text-slate-500">
            No credit card required • Cancel anytime • 24/7 Support
          </p>
          <div className="mt-5">
            <a href="#" className="landing-back-to-top">
              Back to top
              <span aria-hidden="true">↑</span>
            </a>
          </div>
        </motion.div>
      </div>
      <DemoVideoModal isOpen={showDemoVideo} onClose={() => setShowDemoVideo(false)} />
    </section>
  );
};

export default LandingCtaSection;
